import { ChangeDetectionStrategy, Component, OnInit, OnDestroy, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { KeyboardShortcutsService, KeyboardShortcut } from '../../core/services/keyboard-shortcuts.service';
import { Subject, takeUntil, debounceTime } from 'rxjs';

interface Command {
  id: string;
  label: string;
  description?: string;
  icon?: string;
  category: string;
  action: () => void;
  keywords?: string[];
}

@Component({
  selector: 'app-command-palette',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div
      *ngIf="isOpen()"
      class="fixed inset-0 z-50 flex items-start justify-center pt-[20vh] bg-black/50 backdrop-blur-sm"
      (click)="close()"
    >
      <div
        class="w-full max-w-2xl bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden"
        (click)="$event.stopPropagation()"
      >
        <!-- Search Input -->
        <div class="flex items-center gap-3 px-4 py-3 border-b border-slate-200 dark:border-slate-700">
          <svg class="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            [(ngModel)]="searchQuery"
            (ngModelChange)="onSearchChange()"
            (keydown.enter)="executeSelected()"
            (keydown.arrowDown)="navigateDown(); $event.preventDefault()"
            (keydown.arrowUp)="navigateUp(); $event.preventDefault()"
            (keydown.escape)="close()"
            placeholder="Type a command or search..."
            class="flex-1 bg-transparent border-none outline-none text-slate-900 dark:text-white placeholder-slate-400"
            autofocus
          />
          <kbd class="px-2 py-1 text-xs font-semibold text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-700 rounded">Esc</kbd>
        </div>

        <!-- Commands List -->
        <div class="max-h-96 overflow-y-auto">
          <div *ngIf="filteredCommands().length === 0" class="px-4 py-8 text-center text-slate-500">
            <p>No commands found</p>
          </div>
          
          <div *ngFor="let group of groupedCommands(); let groupIndex = index">
            <div class="px-4 py-2 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide bg-slate-50 dark:bg-slate-900">
              {{ group.category }}
            </div>
            <div
              *ngFor="let command of group.commands; let index = index"
              (click)="executeCommand(command)"
              [class]="selectedIndex() === (groupIndex * 1000 + index)
                ? 'px-4 py-3 bg-primary-50 dark:bg-primary-900/20 cursor-pointer'
                : 'px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer'"
            >
              <div class="flex items-center gap-3">
                <span *ngIf="command.icon" [innerHTML]="command.icon" class="w-5 h-5 text-slate-400"></span>
                <div class="flex-1">
                  <div class="font-medium text-slate-900 dark:text-white">{{ command.label }}</div>
                  <div *ngIf="command.description" class="text-sm text-slate-500 dark:text-slate-400">{{ command.description }}</div>
                </div>
                <kbd *ngIf="getShortcutKey(command.id)" class="px-2 py-1 text-xs font-semibold text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-700 rounded">
                  {{ getShortcutKey(command.id) }}
                </kbd>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommandPaletteComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly shortcutsService = inject(KeyboardShortcutsService);
  private readonly destroy$ = new Subject<void>();

  readonly isOpen = signal(false);
  readonly searchQuery = signal('');
  readonly selectedIndex = signal(0);
  readonly commands = signal<Command[]>([]);

  readonly filteredCommands = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();
    if (!query) return this.commands();

    return this.commands().filter((cmd) => {
      const labelMatch = cmd.label.toLowerCase().includes(query);
      const descMatch = cmd.description?.toLowerCase().includes(query);
      const keywordMatch = cmd.keywords?.some((k) => k.toLowerCase().includes(query));
      return labelMatch || descMatch || keywordMatch;
    });
  });

  readonly groupedCommands = computed(() => {
    const filtered = this.filteredCommands();
    const groups = new Map<string, Command[]>();

    filtered.forEach((cmd) => {
      if (!groups.has(cmd.category)) {
        groups.set(cmd.category, []);
      }
      groups.get(cmd.category)!.push(cmd);
    });

    return Array.from(groups.entries()).map(([category, commands]) => ({
      category,
      commands,
    }));
  });

  ngOnInit(): void {
    this.shortcutsService.register(
      {
        key: 'k',
        ctrl: true,
        action: 'open-command-palette',
        description: 'Open command palette',
      },
      () => this.open()
    );

    this.setupDefaultCommands();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.shortcutsService.unregister('open-command-palette');
  }

  private setupDefaultCommands(): void {
    const defaultCommands: Command[] = [
      {
        id: 'new-task',
        label: 'New Task',
        description: 'Create a new task',
        icon: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" /></svg>',
        category: 'Tasks',
        action: () => {
          this.router.navigate(['/tasks', 'new']);
          this.close();
        },
        keywords: ['create', 'add', 'task'],
      },
      {
        id: 'tasks-list',
        label: 'Go to Tasks',
        description: 'Navigate to tasks list',
        icon: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>',
        category: 'Navigation',
        action: () => {
          this.router.navigate(['/tasks']);
          this.close();
        },
        keywords: ['tasks', 'list'],
      },
    ];

    this.commands.set(defaultCommands);
  }

  open(): void {
    this.isOpen.set(true);
    this.searchQuery.set('');
    this.selectedIndex.set(0);
  }

  close(): void {
    this.isOpen.set(false);
    this.searchQuery.set('');
    this.selectedIndex.set(0);
  }

  onSearchChange(): void {
    this.selectedIndex.set(0);
  }

  navigateDown(): void {
    const max = this.filteredCommands().length - 1;
    this.selectedIndex.set(Math.min(this.selectedIndex() + 1, max));
  }

  navigateUp(): void {
    this.selectedIndex.set(Math.max(this.selectedIndex() - 1, 0));
  }

  executeSelected(): void {
    const commands = this.filteredCommands();
    if (commands[this.selectedIndex()]) {
      this.executeCommand(commands[this.selectedIndex()]);
    }
  }

  executeCommand(command: Command): void {
    command.action();
    this.close();
  }

  getShortcutKey(action: string): string | null {
    const shortcuts = this.shortcutsService.getShortcuts();
    const shortcut = shortcuts.find((s) => s.action === action);
    if (!shortcut) return null;

    const parts: string[] = [];
    if (shortcut.ctrl || shortcut.meta) parts.push('⌘');
    if (shortcut.shift) parts.push('⇧');
    if (shortcut.alt) parts.push('⌥');
    parts.push(shortcut.key.toUpperCase());
    return parts.join('');
  }

  registerCommand(command: Command): void {
    this.commands.update((cmds) => [...cmds, command]);
  }
}



































